import { useEffect, useRef, useState } from 'react';
import { cn } from './cn';
import Eyebrow from './Eyebrow';

export type ScrollJackItem = { id: string; title: string; text: string; image?: string };

/**
 * Section à défilement piloté: la zone reste collée sous le header pendant que l'on défile, et la photo (qui
 * déborde jusqu'au bord gauche de l'écran) et le texte changent à chaque palier. Des points verticaux à droite
 * indiquent l'étape et permettent d'y sauter. Sous 1024 px, simple liste empilée.
 */
export default function ScrollJack({ eyebrow, items }: { eyebrow: string; items: ScrollJackItem[] }) {
  const box = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const n = items.length;

  useEffect(() => {
    const onScroll = () => {
      const el = box.current;
      if (!el) return;
      const total = el.offsetHeight - window.innerHeight;
      const p = total > 0 ? Math.min(1, Math.max(0, -el.getBoundingClientRect().top / total)) : 0;
      setActive(Math.min(n - 1, Math.floor(p * n * 0.9999)));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); };
  }, [n]);

  const jump = (i: number) => {
    const el = box.current;
    if (!el) return;
    const total = el.offsetHeight - window.innerHeight;
    const top = el.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: top + (total * (i + 0.5)) / n, behavior: 'smooth' });
  };

  return (
    <section className="bg-white text-black">
      {/* Petite taille: liste empilée */}
      <div className="site-container py-14 lg:hidden">
        <Eyebrow className="mb-8">{eyebrow}</Eyebrow>
        <div className="space-y-12">
          {items.map((it) => (
            <div key={it.id}>
              {it.image && <img src={it.image} alt="" loading="lazy" className="mb-6 aspect-[4/3] w-full object-cover" />}
              <h3 className="font-sans text-[1.75rem] font-bold normal-case leading-tight tracking-normal text-black">{it.title}</h3>
              <p className="mt-3 text-[1.0625rem] leading-relaxed !text-black">{it.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Grande taille: zone collée */}
      <div ref={box} className="relative hidden lg:block" style={{ height: `${n * 85}vh` }}>
        <div className="sticky top-[5.5rem] h-[calc(100svh-5.5rem)] overflow-hidden">
          <div className="site-container absolute inset-x-0 top-0 z-10 pt-12"><Eyebrow>{eyebrow}</Eyebrow></div>
          <div className="grid h-full grid-cols-2">
            <div className="relative overflow-hidden bg-secondary-200">
              {items.map((it, i) => (
                <img
                  key={it.id}
                  src={it.image}
                  alt=""
                  className={cn('absolute inset-0 h-full w-full object-cover transition-all duration-700 ease-out', i === active ? 'translate-y-0 opacity-100' : i < active ? '-translate-y-6 opacity-0' : 'translate-y-6 opacity-0')}
                />
              ))}
            </div>
            <div className="relative flex items-center pl-12 pr-[max(5rem,calc((100vw-72rem)/2+4rem))]">
              {items.map((it, i) => (
                <div key={it.id} aria-hidden={i !== active} className={cn('absolute max-w-md transition-all duration-500 ease-out', i === active ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-5 opacity-0')}>
                  <h3 className="font-sans text-[2.25rem] font-bold normal-case leading-[1.1] tracking-normal text-black">{it.title}</h3>
                  <p className="mt-5 text-[1.0625rem] leading-relaxed !text-black">{it.text}</p>
                </div>
              ))}
              <div className="absolute right-[max(2rem,calc((100vw-72rem)/2))] top-1/2 flex -translate-y-1/2 flex-col gap-3" role="tablist" aria-label="Étapes">
                {items.map((it, i) => (
                  <button key={it.id} role="tab" aria-selected={i === active} aria-label={it.title} onClick={() => jump(i)} className={cn('h-2 w-2 rounded-full transition-colors duration-300', i === active ? 'bg-brand-dark' : 'bg-muted hover:bg-ink')} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
